import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle2, 
  ArrowUp, 
  ArrowDown, 
  Minus, 
  MessageSquare,
  BarChart3,
  Target,
  AlertCircle,
  RefreshCw,
  Info,
  Download,
  Calendar,
  Clock,
  X,
  ThumbsDown,
  ThumbsUp
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";

// Mock data types
type ProductInsights = {
  summary: {
    totalQuestions: number;
    avgSatisfaction: number;
    topProduct: string;
    riskAlerts: number;
    weekOverWeekChange: number;
    lastUpdated: string;
  };
  products: Array<{
    name: string;
    questionCount: number;
    weekOverWeekChange: number;
    satisfactionScore: number;
    trend: 'up' | 'down' | 'stable';
    status: 'excellent' | 'good' | 'needs_work' | 'critical';
    knowledgeGaps: {
      poorAnswers: number;
      unanswered: number;
      priority: 'high' | 'medium' | 'low';
    };
  }>;
  trends: Array<{
    date: string;
    DSCR: number;
    FHA: number;
    VA: number;
    HELOC: number;
    Jumbo: number;
  }>;
  departments: Array<{
    name: string;
    products: Record<string, number>;
    total: number;
  }>;
  risks: Array<{
    product: string;
    level: 'high' | 'medium' | 'low';
    issues: string[];
    count: number;
  }>;
  insights: {
    trendingUp: Array<{product: string; message: string}>;
    needsAttention: Array<{product: string; message: string}>;
    opportunities: Array<{product: string; message: string}>;
  };
  dataRange: {
    start: string;
    end: string;
    period: string;
  };
};


const getStatusBadge = (status: string) => {
  switch (status) {
    case 'excellent':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white">Excellent</Badge>;
    case 'good':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white">Good</Badge>;
    case 'needs_work':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white">Needs Work</Badge>;
    case 'critical':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white">Critical</Badge>;
    default:
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]">{status}</Badge>;
  }
};

const getTrendIcon = (trend: 'up' | 'down' | 'stable', change: number) => {
  if (trend === 'up') {
    return <ArrowUp className="h-3.5 w-3.5 text-[#1a1a1a]" />;
  } else if (trend === 'down') {
    return <ArrowDown className="h-3.5 w-3.5 text-[#1a1a1a]" />;
  } else {
    return <Minus className="h-3.5 w-3.5 text-[#9b9b9b]" />;
  }
};

const getPriorityBadge = (priority: string) => {
  switch (priority) {
    case 'high':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white text-xs">High</Badge>;
    case 'medium':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white text-xs">Medium</Badge>;
    case 'low':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white text-xs">Low</Badge>;
    default:
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] text-xs">{priority}</Badge>;
  }
};

const getRiskLevelBadge = (level: string) => {
  switch (level) {
    case 'high':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white">High Risk</Badge>;
    case 'medium':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white">Medium Risk</Badge>;
    case 'low':
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a] bg-white">Low Risk</Badge>;
    default:
      return <Badge variant="outline" className="border-[#d0d0d0] text-[#1a1a1a]">{level}</Badge>;
  }
};

type RiskDetail = {
  id: string;
  originalQuery: string;
  refinedQuery: string;
  answer: string;
  votes: { up: number; down: number };
  downvoteRate: number;
  satisfactionRate: number;
  approved: boolean;
  createdAt: string;
  sources: any[];
};

type RiskDetails = {
  product: string;
  poorAnswers: RiskDetail[];
  unapprovedResponses: RiskDetail[];
  lowSatisfactionResponses: RiskDetail[];
};

type AnimatedSectionProps = {
  children: ReactNode;
  delay?: number;
};

const AnimatedSection = ({ children, delay = 0 }: AnimatedSectionProps) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1],
      delay,
    }}
    viewport={{ once: true, amount: 0.25 }}
  >
    {children}
  </motion.div>
);

export default function ProductInsightsBoard() {
  const { toast } = useToast();
  const [timeRange, setTimeRange] = useState<string>('30d');
  const [selectedRisk, setSelectedRisk] = useState<{ product: string; level: string } | null>(null);
  const [riskDetailsDialogOpen, setRiskDetailsDialogOpen] = useState(false);
  
  // Fetch risk details when a risk is selected
  const { data: riskDetails, isLoading: riskDetailsLoading } = useQuery<RiskDetails>({
    queryKey: ['/api/admin/product-insights/risk-details', selectedRisk?.product, timeRange],
    queryFn: async () => {
      if (!selectedRisk?.product) return null as any;
      const response = await apiRequest('GET', `/api/admin/product-insights/risk-details?product=${encodeURIComponent(selectedRisk.product)}&timeRange=${timeRange}`);
      return await response.json();
    },
    enabled: !!selectedRisk?.product && riskDetailsDialogOpen,
  });

  const handleRiskClick = (risk: { product: string; level: string }) => {
    setSelectedRisk(risk);
    setRiskDetailsDialogOpen(true);
  };

  // Fetch product insights data from API
  const { data, isLoading, error, refetch, isRefetching } = useQuery<ProductInsights>({
    queryKey: ['/api/admin/product-insights', timeRange],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', `/api/admin/product-insights?timeRange=${timeRange}`);
        // apiRequest throws on error, so if we get here, response is ok
        const data = await response.json();
        return data;
      } catch (err: any) {
        // Extract error message from various error formats
        let errorMessage = 'Failed to fetch product insights';
        
        if (err instanceof Error) {
          errorMessage = err.message;
          // If error message contains status code, extract the actual message
          if (err.message.includes(':')) {
            const parts = err.message.split(':');
            if (parts.length > 1) {
              errorMessage = parts.slice(1).join(':').trim();
            }
          }
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
        
        console.error('[Product Insights] API Error:', err);
        throw new Error(errorMessage || 'Failed to fetch product insights');
      }
    },
    refetchOnWindowFocus: false,
    retry: 1, // Reduced retries to fail faster
  });

  // Refresh data handler
  const handleRefresh = async () => {
    try {
      await refetch();
      toast({
        title: "Data refreshed",
        description: "Product insights have been updated.",
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Failed to refresh product insights. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Export data handler
  const handleExport = () => {
    if (!data) return;
    
    try {
      // Create CSV content
      const csvRows: string[] = [];
      
      // Summary section
      csvRows.push('Product Insights Export');
      csvRows.push(`Generated: ${new Date().toLocaleString()}`);
      csvRows.push(`Time Range: ${data.dataRange.period} (${data.dataRange.start} - ${data.dataRange.end})`);
      csvRows.push('');
      csvRows.push('Summary');
      csvRows.push(`Total Questions,${data.summary.totalQuestions}`);
      csvRows.push(`Average Satisfaction,${data.summary.avgSatisfaction}%`);
      csvRows.push(`Top Product,${data.summary.topProduct}`);
      csvRows.push(`Risk Alerts,${data.summary.riskAlerts}`);
      csvRows.push(`Week-over-Week Change,${data.summary.weekOverWeekChange}%`);
      csvRows.push('');
      
      // Products section
      csvRows.push('Products');
      csvRows.push('Product,Questions,Satisfaction %,Week-over-Week Change %,Trend,Status,Poor Answers,Priority');
      data.products.forEach(p => {
        csvRows.push(`${p.name},${p.questionCount},${p.satisfactionScore},${p.weekOverWeekChange},${p.trend},${p.status},${p.knowledgeGaps.poorAnswers},${p.knowledgeGaps.priority}`);
      });
      csvRows.push('');
      
      // Risks section
      if (data.risks.length > 0) {
        csvRows.push('Risk Alerts');
        csvRows.push('Product,Level,Issues,Count');
        data.risks.forEach(r => {
          csvRows.push(`${r.product},${r.level},"${r.issues.join('; ')}",${r.count}`);
        });
        csvRows.push('');
      }
      
      // Insights section
      csvRows.push('Insights');
      csvRows.push('Category,Product,Message');
      data.insights.trendingUp.forEach(i => {
        csvRows.push(`Trending Up,${i.product},"${i.message}"`);
      });
      data.insights.needsAttention.forEach(i => {
        csvRows.push(`Needs Attention,${i.product},"${i.message}"`);
      });
      data.insights.opportunities.forEach(i => {
        csvRows.push(`Opportunities,${i.product},"${i.message}"`);
      });
      
      // Create blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `product-insights-${data.dataRange.start}-${data.dataRange.end}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Export successful",
        description: "Product insights data has been downloaded.",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-[#1a1a1a] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">Error Loading Product Insights</h3>
              <p className="text-sm text-[#1a1a1a] mb-4">
                {error instanceof Error ? error.message : 'An unexpected error occurred'}
              </p>
              <div className="text-xs text-[#1a1a1a] bg-[#f5f5f5] p-3 rounded border border-[#d0d0d0] mb-4 text-left">
                <p className="font-medium mb-1">Troubleshooting:</p>
                <ul className="list-disc list-inside space-y-1 text-left">
                  <li>Make sure you're logged in as an admin user</li>
                  <li>Check your browser console (F12) for detailed error messages</li>
                  <li>Verify the API endpoint is accessible: /api/admin/product-insights</li>
                  <li>Check that your database tables exist (cached_responses table)</li>
                </ul>
              </div>
              <Button
                onClick={() => refetch()}
                className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a]"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state
  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center p-12">
          <RefreshCw className="h-8 w-8 animate-spin text-[#1a1a1a]" />
          <span className="ml-3 text-[#1a1a1a]">Loading product insights...</span>
        </div>
      </div>
    );
  }

  // Safely format last updated date
  const lastUpdated = data?.summary?.lastUpdated 
    ? format(new Date(data.summary.lastUpdated), 'MMM d, yyyy h:mm a')
    : 'N/A';
  const isRefreshing = isRefetching;

  return (
    <div 
      className="space-y-8 max-w-6xl mx-auto"
      style={{ 
        textAlign: 'left',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* Header Controls */}
      <AnimatedSection>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ textAlign: 'left', width: '100%' }}>
          <div style={{ textAlign: 'left', flex: 1, width: '100%', minWidth: 0 }}>
            <h2 className="text-xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>Product Insights Dashboard</h2>
            <p className="text-xs text-[#1a1a1a] mt-1" style={{ textAlign: 'left', lineHeight: '1.5', wordBreak: 'break-word' }}>
              Data range: {data.dataRange.start ? format(new Date(data.dataRange.start), 'MMM d, yyyy') : 'N/A'} - {data.dataRange.end ? format(new Date(data.dataRange.end), 'MMM d, yyyy') : 'N/A'} ({data.dataRange.period})
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto" style={{ justifyContent: 'flex-start', alignSelf: 'stretch' }}>
            <Select value={timeRange} onValueChange={(value) => {
              setTimeRange(value);
              // Query will automatically refetch when timeRange changes due to queryKey dependency
            }}>
              <SelectTrigger 
                className="w-full sm:w-[140px] border border-[#d0d0d0] bg-white text-[#1a1a1a] touch-manipulation"
                style={{ 
                  fontSize: '16px',
                  textAlign: 'left',
                  minHeight: '44px',
                  padding: '12px 16px',
                  width: '100%'
                }}
              >
                <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#d0d0d0]">
                <SelectItem value="7d" className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }}>Last 7 Days</SelectItem>
                <SelectItem value="30d" className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }}>Last 30 Days</SelectItem>
                <SelectItem value="90d" className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }}>Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation w-full sm:w-auto"
              style={{ 
                minHeight: '44px',
                fontSize: '16px',
                textAlign: 'center',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <RefreshCw className={`h-4 w-4 flex-shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              className="border border-[#d0d0d0] bg-white hover:bg-[#f5f5f5] text-[#1a1a1a] touch-manipulation w-full sm:w-auto"
              style={{ 
                minHeight: '44px',
                fontSize: '16px',
                textAlign: 'center',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Download className="h-4 w-4 flex-shrink-0" />
              <span>Export</span>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      {/* Data Info Banner */}
      <AnimatedSection delay={0.05}>
        <div className="bg-[#f5f5f5] border border-[#d0d0d0] rounded-lg p-3 flex items-start gap-2" style={{ textAlign: 'left' }}>
          <Info className="h-4 w-4 text-[#1a1a1a] mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm text-[#1a1a1a] max-w-3xl" style={{ textAlign: 'left' }}>
            <p className="font-medium mb-1 text-[#1a1a1a]" style={{ textAlign: 'left' }}>Data Transparency</p>
            <p className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
              This view summarizes product performance based on cached responses and user feedback: question volume, satisfaction scores, and knowledge gaps from low-rated answers.
              <span className="font-medium"> Last updated {lastUpdated}</span>
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Section 1: Executive Summary */}
      <AnimatedSection delay={0.08}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" style={{ textAlign: 'left', width: '100%', alignItems: 'stretch' }}>
        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm flex flex-col" style={{ textAlign: 'left', width: '100%', height: '100%' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left', padding: '16px 16px 8px 16px' }}>
            <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.4', margin: 0 }}>Total Questions</CardTitle>
            <MessageSquare className="h-4 w-4 text-[#1a1a1a] flex-shrink-0 ml-2" />
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-between" style={{ textAlign: 'left', padding: '0 16px 16px 16px' }}>
            <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.2', marginBottom: '4px' }}>{data.summary.totalQuestions.toLocaleString()}</div>
            <p className="text-xs text-[#1a1a1a] mt-1" style={{ textAlign: 'left', lineHeight: '1.5' }}>Unique questions answered</p>
          </CardContent>
        </Card>

        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm flex flex-col" style={{ textAlign: 'left', width: '100%', height: '100%' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left', padding: '16px 16px 8px 16px' }}>
            <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.4', margin: 0 }}>Avg Satisfaction</CardTitle>
            <Target className="h-4 w-4 text-[#1a1a1a] flex-shrink-0 ml-2" />
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-between" style={{ textAlign: 'left', padding: '0 16px 16px 16px' }}>
            <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.2', marginBottom: '4px' }}>{data.summary.avgSatisfaction}%</div>
            <p className="text-xs text-[#1a1a1a] mt-1 flex items-center gap-1" style={{ textAlign: 'left', lineHeight: '1.5' }}>
              <TrendingUp className="h-3 w-3 flex-shrink-0" />
              {data.summary.weekOverWeekChange >= 0 ? '+' : ''}{data.summary.weekOverWeekChange}% Week over Week
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm flex flex-col" style={{ textAlign: 'left', width: '100%', height: '100%' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left', padding: '16px 16px 8px 16px' }}>
            <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.4', margin: 0 }}>Top Product</CardTitle>
            <BarChart3 className="h-4 w-4 text-[#1a1a1a] flex-shrink-0 ml-2" />
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-between" style={{ textAlign: 'left', padding: '0 16px 16px 16px' }}>
            <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.2', marginBottom: '4px', wordBreak: 'break-word' }}>{data.summary.topProduct}</div>
            {data.products.length > 0 && (() => {
              const topProductData = data.products.find(p => p.name === data.summary.topProduct);
              return topProductData ? (
                <p className="text-xs text-[#1a1a1a] mt-1 flex items-center gap-1" style={{ textAlign: 'left', lineHeight: '1.5' }}>
                  {topProductData.weekOverWeekChange >= 0 ? <TrendingUp className="h-3 w-3 flex-shrink-0" /> : <TrendingDown className="h-3 w-3 flex-shrink-0" />}
                  {topProductData.weekOverWeekChange >= 0 ? '+' : ''}{topProductData.weekOverWeekChange}% change
                </p>
              ) : null;
            })()}
          </CardContent>
        </Card>

        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm flex flex-col" style={{ textAlign: 'left', width: '100%', height: '100%' }}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" style={{ textAlign: 'left', padding: '16px 16px 8px 16px' }}>
            <CardTitle className="text-sm font-medium text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.4', margin: 0 }}>Risk Alerts</CardTitle>
            <AlertCircle className="h-4 w-4 text-[#1a1a1a] flex-shrink-0 ml-2" />
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-between" style={{ textAlign: 'left', padding: '0 16px 16px 16px' }}>
            <div className="text-2xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.2', marginBottom: '4px' }}>{data.summary.riskAlerts}</div>
            <p className="text-xs text-[#1a1a1a] mt-1" style={{ textAlign: 'left', lineHeight: '1.5' }}>Requires attention</p>
          </CardContent>
        </Card>
      </div>
      </AnimatedSection>

      {/* Section 2: Insights & Recommendations */}
      <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left', width: '100%' }}>
        <CardHeader style={{ textAlign: 'left', padding: '16px 16px 12px 16px' }}>
          <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>Insights & Recommendations</CardTitle>
          <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5', margin: 0 }}>AI-generated insights based on data trends and patterns</CardDescription>
        </CardHeader>
        <CardContent style={{ textAlign: 'left', padding: '0 16px 16px 16px' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ textAlign: 'left', width: '100%', alignItems: 'stretch' }}>
            {/* Trending Up */}
            <div className="p-4 bg-[#f5f5f5] rounded-lg border border-[#d0d0d0] flex flex-col" style={{ textAlign: 'left', width: '100%', height: '100%' }}>
              <div className="flex items-center gap-2 mb-3" style={{ textAlign: 'left' }}>
                <TrendingUp className="h-5 w-5 text-[#1a1a1a] flex-shrink-0" />
                <h3 className="font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.3', margin: 0 }}>Trending Up</h3>
              </div>
              <ul className="space-y-2.5" style={{ textAlign: 'left', listStylePosition: 'outside', paddingLeft: '20px', margin: 0 }}>
                {data.insights.trendingUp.length === 0 ? (
                  <li className="text-sm text-[#1a1a1a] text-gray-500" style={{ textAlign: 'left', lineHeight: '1.5', listStyle: 'none', paddingLeft: 0 }}>No trending products</li>
                ) : (
                  data.insights.trendingUp.map((insight, i) => (
                    <li key={i} className="text-sm text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.6', wordBreak: 'break-word' }}>
                      <span className="font-semibold text-[#1a1a1a]">{insight.product}:</span> {insight.message}
                    </li>
                  ))
                )}
              </ul>
            </div>

            {/* Needs Attention */}
            <div className="p-4 bg-[#f5f5f5] rounded-lg border border-[#d0d0d0] flex flex-col" style={{ textAlign: 'left', width: '100%', height: '100%' }}>
              <div className="flex items-center gap-2 mb-3" style={{ textAlign: 'left' }}>
                <AlertTriangle className="h-5 w-5 text-[#1a1a1a] flex-shrink-0" />
                <h3 className="font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.3', margin: 0 }}>Needs Attention</h3>
              </div>
              <ul className="space-y-2.5" style={{ textAlign: 'left', listStylePosition: 'outside', paddingLeft: '20px', margin: 0 }}>
                {data.insights.needsAttention.length === 0 ? (
                  <li className="text-sm text-[#1a1a1a] text-gray-500" style={{ textAlign: 'left', lineHeight: '1.5', listStyle: 'none', paddingLeft: 0 }}>No products need attention</li>
                ) : (
                  data.insights.needsAttention.map((insight, i) => (
                    <li key={i} className="text-sm text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.6', wordBreak: 'break-word' }}>
                      <span className="font-semibold text-[#1a1a1a]">{insight.product}:</span> {insight.message}
                    </li>
                  ))
                )}
              </ul>
            </div>

            {/* Opportunities */}
            <div className="p-4 bg-[#f5f5f5] rounded-lg border border-[#d0d0d0] flex flex-col" style={{ textAlign: 'left', width: '100%', height: '100%' }}>
              <div className="flex items-center gap-2 mb-3" style={{ textAlign: 'left' }}>
                <Target className="h-5 w-5 text-[#1a1a1a] flex-shrink-0" />
                <h3 className="font-semibold text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.3', margin: 0 }}>Opportunities</h3>
              </div>
              <ul className="space-y-2.5" style={{ textAlign: 'left', listStylePosition: 'outside', paddingLeft: '20px', margin: 0 }}>
                {data.insights.opportunities.length === 0 ? (
                  <li className="text-sm text-[#1a1a1a] text-gray-500" style={{ textAlign: 'left', lineHeight: '1.5', listStyle: 'none', paddingLeft: 0 }}>No opportunities identified</li>
                ) : (
                  data.insights.opportunities.map((insight, i) => (
                    <li key={i} className="text-sm text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.6', wordBreak: 'break-word' }}>
                      <span className="font-semibold text-[#1a1a1a]">{insight.product}:</span> {insight.message}
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Product Performance Grid */}
      <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left', width: '100%' }}>
        <CardHeader style={{ textAlign: 'left', padding: '16px 16px 12px 16px' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" style={{ width: '100%' }}>
            <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
              <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>Product Performance</CardTitle>
              <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5', margin: 0 }}>Question volume and satisfaction by product type</CardDescription>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#1a1a1a] flex-shrink-0" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Clock className="h-3 w-3 flex-shrink-0" />
              <span>Updated {lastUpdated}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent style={{ textAlign: 'left', padding: '0 16px 16px 16px' }}>
          {data.products.length === 0 ? (
            <div className="text-center py-8 text-[#1a1a1a]" style={{ textAlign: 'center' }}>
              <p style={{ textAlign: 'center' }}>No product data available for the selected time range</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" style={{ textAlign: 'left', width: '100%', alignItems: 'stretch' }}>
              {data.products.map((product) => (
              <div
                key={product.name}
                className="p-4 border border-[#d0d0d0] rounded-lg bg-white active:bg-[#f5f5f5] transition-colors touch-manipulation flex flex-col"
                style={{ textAlign: 'left', minHeight: '140px', width: '100%', height: '100%' }}
              >
                <div className="text-sm font-semibold text-[#1a1a1a] mb-3" style={{ textAlign: 'left', lineHeight: '1.3', wordBreak: 'break-word' }}>{product.name}</div>
                <div className="text-2xl font-bold text-[#1a1a1a] mb-2" style={{ textAlign: 'left', lineHeight: '1.2' }}>{product.questionCount}</div>
                <div className="flex items-center gap-1.5 text-xs mb-2" style={{ textAlign: 'left' }}>
                  {getTrendIcon(product.trend, product.weekOverWeekChange)}
                  <span className="text-[#1a1a1a] font-medium" style={{ textAlign: 'left', lineHeight: '1.4' }}>
                    {product.weekOverWeekChange >= 0 ? "+" : ""}{product.weekOverWeekChange}%
                  </span>
                </div>
                <div className="text-xs text-[#1a1a1a] mb-2" style={{ textAlign: 'left', lineHeight: '1.5' }}>{product.satisfactionScore}% satisfaction</div>
                <div className="mt-2" style={{ textAlign: 'left' }}>{getStatusBadge(product.status)}</div>
              </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Product Trends Chart */}
      <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left', width: '100%' }}>
        <CardHeader style={{ textAlign: 'left', padding: '16px 16px 12px 16px' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" style={{ width: '100%' }}>
            <div style={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
              <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>Product Question Trends</CardTitle>
              <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5', margin: 0 }}>Weekly question volume over selected time period</CardDescription>
            </div>
            <div className="text-xs text-[#1a1a1a] flex-shrink-0" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              {data.dataRange.period}
            </div>
          </div>
        </CardHeader>
        <CardContent style={{ textAlign: 'left' }}>
          {data.trends.length === 0 ? (
            <div className="h-[250px] sm:h-[350px] flex items-center justify-center text-[#1a1a1a]" style={{ textAlign: 'center' }}>
              <p style={{ textAlign: 'center' }}>No trend data available for the selected time range</p>
            </div>
          ) : (() => {
            // Get all unique products from trends data
            const allProductKeys = new Set<string>();
            data.trends.forEach(trend => {
              Object.keys(trend).forEach(key => {
                if (key !== 'date') {
                  allProductKeys.add(key);
                }
              });
            });
            
            // Color palette for products
            const colors = [
              "#3b82f6", // blue
              "#10b981", // green
              "#f59e0b", // amber
              "#ef4444", // red
              "#8b5cf6", // purple
              "#ec4899", // pink
              "#06b6d4", // cyan
              "#84cc16", // lime
            ];
            
            // Build config and lines dynamically
            const chartConfig: Record<string, { label: string; color: string }> = {};
            const productArray = Array.from(allProductKeys);
            productArray.forEach((product, index) => {
              chartConfig[product] = {
                label: product,
                color: colors[index % colors.length],
              };
            });
            
            return (
              <div className="w-full overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <ChartContainer
                config={chartConfig}
                  className="h-[250px] sm:h-[350px] w-full min-w-[600px]"
              >
                  <LineChart data={data.trends} width={Math.max(600, window.innerWidth - 80)} height={window.innerWidth < 640 ? 250 : 350}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6b7280"
                      style={{ fontSize: window.innerWidth < 640 ? '10px' : '12px' }}
                      angle={window.innerWidth < 640 ? -45 : 0}
                      textAnchor={window.innerWidth < 640 ? 'end' : 'middle'}
                      height={window.innerWidth < 640 ? 60 : 30}
                  />
                  <YAxis 
                    stroke="#6b7280"
                      style={{ fontSize: window.innerWidth < 640 ? '10px' : '12px' }}
                      width={window.innerWidth < 640 ? 40 : 60}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  {productArray.map((product, index) => (
                    <Line 
                      key={product}
                      type="monotone" 
                      dataKey={product} 
                      stroke={colors[index % colors.length]} 
                        strokeWidth={window.innerWidth < 640 ? 2 : 2.5} 
                        dot={{ r: window.innerWidth < 640 ? 3 : 4 }} 
                    />
                  ))}
                </LineChart>
              </ChartContainer>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Section 5 & 6: Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ textAlign: 'left', width: '100%' }}>
        {/* Product Satisfaction Table */}
        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left', width: '100%' }}>
          <CardHeader style={{ textAlign: 'left', padding: '16px 16px 12px 16px' }}>
            <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>Product Satisfaction</CardTitle>
            <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5', margin: 0 }}>Performance metrics calculated from user votes</CardDescription>
          </CardHeader>
          <CardContent style={{ textAlign: 'left' }}>
            {data.products.length === 0 ? (
              <div className="text-center py-8 text-[#1a1a1a]" style={{ textAlign: 'center' }}>
                <p style={{ textAlign: 'center' }}>No product data available for the selected time range</p>
              </div>
            ) : (
              <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[#1a1a1a]" style={{ textAlign: 'left', fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Product</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Questions</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Satisfaction</TableHead>
                      <TableHead className="text-center text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Trend</TableHead>
                      <TableHead className="text-center text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.products.map((product) => (
                    <TableRow key={product.name} className="active:bg-[#f5f5f5]">
                      <TableCell className="font-medium text-[#1a1a1a] text-sm" style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px' }}>{product.name}</TableCell>
                      <TableCell className="text-right text-[#1a1a1a]" style={{ padding: '12px 8px', fontSize: '14px' }}>{product.questionCount.toLocaleString()}</TableCell>
                      <TableCell className="text-right" style={{ padding: '12px 8px', fontSize: '14px' }}>
                        <span className="font-semibold text-[#1a1a1a]">
                          {product.satisfactionScore}%
                        </span>
                      </TableCell>
                      <TableCell className="text-center" style={{ padding: '12px 8px' }}>
                        <div className="flex items-center justify-center gap-1">
                          {getTrendIcon(product.trend, product.weekOverWeekChange)}
                          <span className="text-xs font-medium text-[#1a1a1a]">
                            {product.weekOverWeekChange >= 0 ? "+" : ""}{product.weekOverWeekChange}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center" style={{ padding: '12px 8px' }}>{getStatusBadge(product.status)}</TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Knowledge Gaps */}
        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left', width: '100%' }}>
          <CardHeader style={{ textAlign: 'left', padding: '16px 16px 12px 16px' }}>
            <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>Knowledge Gaps</CardTitle>
            <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5', margin: 0 }}>Identified from low-rated responses (&lt;60% satisfaction)</CardDescription>
          </CardHeader>
          <CardContent style={{ textAlign: 'left' }}>
            {data.products.length === 0 ? (
              <div className="text-center py-8 text-[#1a1a1a]" style={{ textAlign: 'center' }}>
                <p style={{ textAlign: 'center' }}>No product data available for the selected time range</p>
              </div>
            ) : (
              <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[#1a1a1a]" style={{ textAlign: 'left', fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Product</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Poor Answers</TableHead>
                      <TableHead className="text-right text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Unanswered</TableHead>
                      <TableHead className="text-center text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Priority</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.products.map((product) => (
                    <TableRow key={product.name} className="active:bg-[#f5f5f5]">
                      <TableCell className="font-medium text-[#1a1a1a] text-sm" style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px' }}>{product.name}</TableCell>
                      <TableCell className="text-right text-[#1a1a1a]" style={{ padding: '12px 8px', fontSize: '14px' }}>{product.knowledgeGaps.poorAnswers}</TableCell>
                      <TableCell className="text-right text-[#1a1a1a]" style={{ padding: '12px 8px', fontSize: '14px' }}>{product.knowledgeGaps.unanswered}</TableCell>
                      <TableCell className="text-center" style={{ padding: '12px 8px' }}>{getPriorityBadge(product.knowledgeGaps.priority)}</TableCell>
                    </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 7: Department Breakdown */}
      <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left', width: '100%' }}>
        <CardHeader style={{ textAlign: 'left', padding: '16px 16px 12px 16px' }}>
          <CardTitle className="text-[#1a1a1a]" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>Product Usage by Department</CardTitle>
          <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5', margin: 0 }}>Question distribution across departments (from user conversations)</CardDescription>
        </CardHeader>
        <CardContent style={{ textAlign: 'left' }}>
          {data.departments.length === 0 ? (
            <div className="text-center py-8 text-[#1a1a1a]" style={{ textAlign: 'center' }}>
              <p style={{ textAlign: 'center' }}>No department data available for the selected time range</p>
            </div>
          ) : (() => {
            // Get all unique products from department data
            const allProducts = new Set<string>();
            data.departments.forEach(dept => {
              Object.keys(dept.products).forEach(product => {
                allProducts.add(product);
              });
            });
            const productArray = Array.from(allProducts).sort();
            
            return (
              <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[#1a1a1a]" style={{ textAlign: 'left', fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10 }}>Department</TableHead>
                      {productArray.map(product => (
                        <TableHead key={product} className="text-right text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>{product}</TableHead>
                      ))}
                      <TableHead className="text-right text-[#1a1a1a]" style={{ fontSize: '14px', padding: '12px 8px', whiteSpace: 'nowrap' }}>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.departments.map((dept) => (
                      <TableRow key={dept.name} className="active:bg-[#f5f5f5]">
                        <TableCell className="font-medium text-[#1a1a1a] text-sm" style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 5 }}>{dept.name}</TableCell>
                        {productArray.map(product => (
                          <TableCell key={product} className="text-right text-[#1a1a1a]" style={{ padding: '12px 8px', fontSize: '14px' }}>
                            {dept.products[product] || 0}
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-semibold text-[#1a1a1a]" style={{ padding: '12px 8px', fontSize: '14px' }}>{dept.total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Section 8: Risk Alerts */}
      {data.risks.length > 0 && (
        <Card className="rounded-lg border border-[#d0d0d0] bg-white shadow-sm" style={{ textAlign: 'left', width: '100%' }}>
          <CardHeader style={{ textAlign: 'left', padding: '16px 16px 12px 16px' }}>
            <CardTitle className="text-[#1a1a1a] flex items-center gap-2" style={{ textAlign: 'left', marginBottom: '4px', lineHeight: '1.3' }}>
              <AlertTriangle className="h-5 w-5 text-[#1a1a1a] flex-shrink-0" />
              <span>Product Risk Alerts</span>
            </CardTitle>
            <CardDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5', margin: 0 }}>
              High-priority issues requiring immediate attention (based on downvote rates and unapproved responses)
            </CardDescription>
          </CardHeader>
          <CardContent style={{ textAlign: 'left' }}>
            <div className="space-y-3" style={{ textAlign: 'left' }}>
              {data.risks.map((risk, index) => (
                <div 
                  key={index} 
                  onClick={() => handleRiskClick(risk)}
                  className="p-4 bg-[#f5f5f5] rounded-lg border border-[#d0d0d0] cursor-pointer active:bg-[#e8e8e8] transition-colors touch-manipulation"
                  style={{ textAlign: 'left', minHeight: '60px' }}
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-2" style={{ textAlign: 'left' }}>
                    <div className="flex items-center gap-2 flex-wrap" style={{ textAlign: 'left' }}>
                      {getRiskLevelBadge(risk.level)}
                      <span className="font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>{risk.product}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" style={{ textAlign: 'right' }}>
                      <span className="text-sm text-[#1a1a1a]" style={{ textAlign: 'right' }}>{risk.count} issue{risk.count !== 1 ? 's' : ''}</span>
                      <Info className="h-4 w-4 text-[#1a1a1a] opacity-60 flex-shrink-0" />
                    </div>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-[#1a1a1a] ml-1" style={{ textAlign: 'left', listStylePosition: 'inside' }}>
                    {risk.issues.map((issue, i) => (
                      <li key={i} className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5' }}>{issue}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk Details Dialog */}
      <Dialog open={riskDetailsDialogOpen} onOpenChange={setRiskDetailsDialogOpen}>
        <DialogContent 
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          style={{
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <DialogHeader style={{ textAlign: 'left' }}>
            <DialogTitle className="text-[#1a1a1a] flex items-center gap-2" style={{ textAlign: 'left' }}>
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <span style={{ textAlign: 'left' }}>Risk Details: {selectedRisk?.product}</span>
            </DialogTitle>
            <DialogDescription className="text-[#1a1a1a]" style={{ textAlign: 'left', lineHeight: '1.5' }}>
              Questions and answers contributing to risk alerts for {selectedRisk?.product}
            </DialogDescription>
          </DialogHeader>
          
          {riskDetailsLoading ? (
            <div className="flex items-center justify-center py-8" style={{ textAlign: 'center' }}>
              <RefreshCw className="h-6 w-6 animate-spin text-[#1a1a1a] flex-shrink-0" />
              <span className="ml-2 text-[#1a1a1a]" style={{ textAlign: 'left' }}>Loading risk details...</span>
            </div>
          ) : riskDetails ? (
            <Tabs defaultValue="poorAnswers" className="w-full" style={{ textAlign: 'left' }}>
              <TabsList className="grid w-full grid-cols-3" style={{ minHeight: '44px' }}>
                <TabsTrigger 
                  value="poorAnswers"
                  className="touch-manipulation"
                  style={{ fontSize: '14px', minHeight: '44px', textAlign: 'center' }}
                >
                  Poor Answers ({riskDetails.poorAnswers.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="unapproved"
                  className="touch-manipulation"
                  style={{ fontSize: '14px', minHeight: '44px', textAlign: 'center' }}
                >
                  Unapproved ({riskDetails.unapprovedResponses.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="lowSatisfaction"
                  className="touch-manipulation"
                  style={{ fontSize: '14px', minHeight: '44px', textAlign: 'center' }}
                >
                  Low Satisfaction ({riskDetails.lowSatisfactionResponses.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="poorAnswers" className="mt-4" style={{ textAlign: 'left' }}>
                <div className="space-y-4" style={{ textAlign: 'left' }}>
                  {riskDetails.poorAnswers.length === 0 ? (
                    <p className="text-[#1a1a1a] text-center py-4" style={{ textAlign: 'center' }}>No poor answers found</p>
                  ) : (
                    riskDetails.poorAnswers.map((item) => (
                      <Card key={item.id} className="border border-[#d0d0d0] bg-white" style={{ textAlign: 'left' }}>
                        <CardContent className="p-4" style={{ textAlign: 'left' }}>
                          <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between mb-2 gap-3">
                            <div className="flex-1" style={{ textAlign: 'left', minWidth: 0 }}>
                              <p className="font-semibold text-[#1a1a1a] mb-1" style={{ textAlign: 'left' }}>Question:</p>
                              <p className="text-sm text-[#1a1a1a] mb-3" style={{ textAlign: 'left', lineHeight: '1.5', wordBreak: 'break-word' }}>{item.originalQuery}</p>
                              <p className="font-semibold text-[#1a1a1a] mb-1" style={{ textAlign: 'left' }}>Answer:</p>
                              <p className="text-sm text-[#1a1a1a] mb-3 whitespace-pre-wrap" style={{ textAlign: 'left', lineHeight: '1.5', wordBreak: 'break-word' }}>{item.answer}</p>
                            </div>
                            <div className="flex flex-col items-end sm:items-end gap-2 sm:ml-4 flex-shrink-0" style={{ textAlign: 'right' }}>
                              <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50" style={{ fontSize: '12px' }}>
                                {item.downvoteRate}% Downvote Rate
                              </Badge>
                              <div className="flex items-center gap-2 text-xs text-[#1a1a1a]" style={{ textAlign: 'right' }}>
                                <ThumbsDown className="h-4 w-4 flex-shrink-0" />
                                <span>{item.votes.down}</span>
                                <ThumbsUp className="h-4 w-4 ml-2 flex-shrink-0" />
                                <span>{item.votes.up}</span>
                              </div>
                              <span className="text-xs text-[#1a1a1a]" style={{ textAlign: 'right' }}>
                                {format(new Date(item.createdAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="unapproved" className="mt-4" style={{ textAlign: 'left' }}>
                <div className="space-y-4" style={{ textAlign: 'left' }}>
                  {riskDetails.unapprovedResponses.length === 0 ? (
                    <p className="text-[#1a1a1a] text-center py-4" style={{ textAlign: 'center' }}>No unapproved responses found</p>
                  ) : (
                    riskDetails.unapprovedResponses.map((item) => (
                      <Card key={item.id} className="border border-[#d0d0d0] bg-white" style={{ textAlign: 'left' }}>
                        <CardContent className="p-4" style={{ textAlign: 'left' }}>
                          <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between mb-2 gap-3">
                            <div className="flex-1" style={{ textAlign: 'left', minWidth: 0 }}>
                              <p className="font-semibold text-[#1a1a1a] mb-1" style={{ textAlign: 'left' }}>Question:</p>
                              <p className="text-sm text-[#1a1a1a] mb-3" style={{ textAlign: 'left', lineHeight: '1.5', wordBreak: 'break-word' }}>{item.originalQuery}</p>
                              <p className="font-semibold text-[#1a1a1a] mb-1" style={{ textAlign: 'left' }}>Answer:</p>
                              <p className="text-sm text-[#1a1a1a] mb-3 whitespace-pre-wrap" style={{ textAlign: 'left', lineHeight: '1.5', wordBreak: 'break-word' }}>{item.answer}</p>
                            </div>
                            <div className="flex flex-col items-end sm:items-end gap-2 sm:ml-4 flex-shrink-0" style={{ textAlign: 'right' }}>
                              <Badge variant="outline" className="border-yellow-300 text-yellow-700 bg-yellow-50" style={{ fontSize: '12px' }}>
                                Unapproved
                              </Badge>
                              <div className="flex items-center gap-2 text-xs text-[#1a1a1a]" style={{ textAlign: 'right' }}>
                                <ThumbsDown className="h-4 w-4 flex-shrink-0" />
                                <span>{item.votes.down}</span>
                                <ThumbsUp className="h-4 w-4 ml-2 flex-shrink-0" />
                                <span>{item.votes.up}</span>
                              </div>
                              <span className="text-xs text-[#1a1a1a]" style={{ textAlign: 'right' }}>
                                {format(new Date(item.createdAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="lowSatisfaction" className="mt-4" style={{ textAlign: 'left' }}>
                <div className="space-y-4" style={{ textAlign: 'left' }}>
                  {riskDetails.lowSatisfactionResponses.length === 0 ? (
                    <p className="text-[#1a1a1a] text-center py-4" style={{ textAlign: 'center' }}>No low satisfaction responses found</p>
                  ) : (
                    riskDetails.lowSatisfactionResponses.map((item) => (
                      <Card key={item.id} className="border border-[#d0d0d0] bg-white" style={{ textAlign: 'left' }}>
                        <CardContent className="p-4" style={{ textAlign: 'left' }}>
                          <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between mb-2 gap-3">
                            <div className="flex-1" style={{ textAlign: 'left', minWidth: 0 }}>
                              <p className="font-semibold text-[#1a1a1a] mb-1" style={{ textAlign: 'left' }}>Question:</p>
                              <p className="text-sm text-[#1a1a1a] mb-3" style={{ textAlign: 'left', lineHeight: '1.5', wordBreak: 'break-word' }}>{item.originalQuery}</p>
                              <p className="font-semibold text-[#1a1a1a] mb-1" style={{ textAlign: 'left' }}>Answer:</p>
                              <p className="text-sm text-[#1a1a1a] mb-3 whitespace-pre-wrap" style={{ textAlign: 'left', lineHeight: '1.5', wordBreak: 'break-word' }}>{item.answer}</p>
                            </div>
                            <div className="flex flex-col items-end sm:items-end gap-2 sm:ml-4 flex-shrink-0" style={{ textAlign: 'right' }}>
                              <Badge variant="outline" className="border-orange-300 text-orange-700 bg-orange-50" style={{ fontSize: '12px' }}>
                                {item.satisfactionRate}% Satisfaction
                              </Badge>
                              <div className="flex items-center gap-2 text-xs text-[#1a1a1a]" style={{ textAlign: 'right' }}>
                                <ThumbsDown className="h-4 w-4 flex-shrink-0" />
                                <span>{item.votes.down}</span>
                                <ThumbsUp className="h-4 w-4 ml-2 flex-shrink-0" />
                                <span>{item.votes.up}</span>
                              </div>
                              <span className="text-xs text-[#1a1a1a]" style={{ textAlign: 'right' }}>
                                {format(new Date(item.createdAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <p className="text-[#1a1a1a] text-center py-4" style={{ textAlign: 'center' }}>No risk details available</p>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
